/**
 * Third-party brand color constants.
 *
 * Lives outside the design-system token system because these are not
 * LarkPM brand colors — they are external trademarks (OAuth providers,
 * partner logos, etc.) that must render in their specified palette to
 * satisfy brand-asset guidelines.
 *
 * This file is allowlisted from the design-system lint + audit. Do NOT
 * add LarkPM-internal colors here; those belong in src/index.css
 * (tokens) or src/lib/tokens.ts (TypeScript token export).
 *
 * Usage
 *   import { GOOGLE_BRAND } from '@/lib/brand-colors';
 *   <path fill={GOOGLE_BRAND.blue} />
 *
 * Prefer wrapping brand logos in `<src/components/brand/X.tsx>` so the
 * hex stays inside one file instead of being spread across consumers.
 */

/**
 * Google's official brand palette for the "Sign in with Google" button.
 * Pinned by Google's brand-asset guidelines — do not substitute.
 */
export const GOOGLE_BRAND = {
  blue: '#4285F4',
  green: '#34A853',
  yellow: '#FBBC05',
  red: '#EA4335',
} as const;

/**
 * LarkPM referral / invite accent. Visible in the InviteTeamMember
 * sidebar background. NOTE: the token version (`--brand-yellow` /
 * `bg-brand-yellow`) is the preferred consumer; this export is for
 * non-Tailwind contexts (canvas, PDF, OG images) that need the raw hex.
 */
export const LARKPM_BRAND = {
  yellow: '#FEBE10',
  yellowDark: '#E5A800', // hover state for the referral CTA
} as const;

/**
 * Type aliases for consumers that want a tight color constraint.
 */
export type GoogleBrandColor = (typeof GOOGLE_BRAND)[keyof typeof GOOGLE_BRAND];
export type LarkPmBrandColor = (typeof LARKPM_BRAND)[keyof typeof LARKPM_BRAND];
